import React, { useMemo, useState } from 'react';
import { useApp } from '../../context/AppContext';
import Thumb from '../../components/Thumb';
import AddGearDialog from '../../components/AddGearDialog';
import { DaySpanBadge, getEffectiveSpan, spanCoversDay } from '../../components/ProjectDays';
import { Plus, Package, Camera, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * "Gear List" tab on a project page. Shows kits and individual items added to
 * the project, with day-span editing (when day_count > 1), multi-qty +/-
 * controls, and a search-add dialog.
 *
 * Per-day filtering is driven by the `selectedDay` prop passed down from
 * the parent. `null` means "All days".
 */
export default function GearListTab({ project, selectedDay }) {
  const {
    items,
    kits,
    projectAddItem,
    projectRemoveItem,
    projectRemoveKit,
    projectClearGear,
    expandProjectGear,
    refreshProjects,
    categoryColor,
  } = useApp();
  const [addOpen, setAddOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const dayCount = Math.max(1, project.dayCount || 1);
  const filterByDay = (code) =>
    selectedDay == null || spanCoversDay(getEffectiveSpan(project, code), selectedDay);

  // Aggregate direct item_codes by code so multi-qty entries render as a single row.
  const directQtyByCode = useMemo(() => {
    const m = new Map();
    (project.itemCodes || []).forEach((c) => m.set(c, (m.get(c) || 0) + 1));
    return m;
  }, [project.itemCodes]);
  const uniqueItemCodes = useMemo(() => Array.from(directQtyByCode.keys()), [directQtyByCode]);

  const visibleKitCodes = project.kitCodes.filter(filterByDay);
  const visibleItemCodes = uniqueItemCodes.filter(filterByDay);
  const hiddenCount =
    project.kitCodes.length + uniqueItemCodes.length -
    visibleKitCodes.length - visibleItemCodes.length;

  const expanded = expandProjectGear(project);
  const totalUnits = expanded.reduce((s, e) => s + e.qty, 0);

  const handleSpanSaved = () => {
    if (refreshProjects) refreshProjects();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-xs text-neutral-400">
          {project.kitCodes.length} kit{project.kitCodes.length === 1 ? '' : 's'} ·{' '}
          {uniqueItemCodes.length} extra item{uniqueItemCodes.length === 1 ? '' : 's'} ·{' '}
          <span className="text-white">{totalUnits}</span> total units
          {hiddenCount > 0 && (
            <span className="ml-2 text-amber-400" data-testid="hidden-count-note">
              · {hiddenCount} hidden for Day {selectedDay}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(project.kitCodes.length > 0 || project.itemCodes.length > 0) && (
            <button
              onClick={async () => {
                const total = project.kitCodes.length + (project.itemCodes || []).length;
                if (!window.confirm(
                  `Remove ALL gear from this project?\n\n` +
                  `This will clear ${project.kitCodes.length} kit(s) and ${totalUnits} item unit(s). ` +
                  `Use this after wrapping a shoot to reset the gear list.`
                )) return;
                setClearing(true);
                try {
                  await projectClearGear(project.id);
                  toast.success(`Cleared ${total} entr${total === 1 ? 'y' : 'ies'} from gear list`);
                } catch (e) {
                  toast.error(e.message || 'Could not clear gear');
                } finally {
                  setClearing(false);
                }
              }}
              disabled={clearing}
              className="rounded-md px-3 py-1.5 text-xs flex items-center gap-2 border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
              data-testid="gear-remove-all-btn"
            >
              <Trash2 size={13} /> {clearing ? 'Removing…' : 'Remove All'}
            </button>
          )}
          <button
            onClick={() => setAddOpen(true)}
            className="btn-amber rounded-md px-3 py-1.5 text-xs flex items-center gap-2"
          >
            <Plus size={13} /> Add Gear
          </button>
        </div>
      </div>

      {project.kitCodes.length === 0 && project.itemCodes.length === 0 ? (
        <div className="surface rounded-lg p-10 text-center">
          <Package size={28} className="mx-auto text-neutral-700 mb-3" />
          <p className="text-sm text-neutral-400 mb-4">No gear added yet.</p>
          <button
            onClick={() => setAddOpen(true)}
            className="btn-amber rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-2"
          >
            <Plus size={13} /> Browse Inventory
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleKitCodes.length > 0 && (
            <div className="surface rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 border-b border-neutral-900 flex items-center gap-2 bg-neutral-950">
                <Package size={13} className="text-amber-400" />
                <span className="text-xs font-semibold text-white tracking-wider uppercase">Kits</span>
                <span className="text-[11px] text-neutral-500 ml-auto">{visibleKitCodes.length}</span>
              </div>
              <div className="divide-y divide-neutral-900">
                {visibleKitCodes.map((code) => {
                  const kit = kits.find((k) => k.code === code);
                  if (!kit) return null;
                  return (
                    <div key={code} className="px-4 py-3 flex items-center gap-3 row-hover">
                      <Thumb src={kit.image} name={kit.name} size="md" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white">{kit.name}</div>
                        <div className="text-[11px] text-neutral-500">
                          <span className="font-mono">{kit.code}</span> · contains {kit.itemCodes.length} items
                        </div>
                      </div>
                      {dayCount > 1 && (
                        <DaySpanBadge
                          project={project}
                          code={code}
                          onProjectUpdate={handleSpanSaved}
                        />
                      )}
                      <button
                        onClick={() => {
                          projectRemoveKit(project.id, code);
                          toast.success(`${kit.name} removed`);
                        }}
                        className="text-[11px] text-rose-400 hover:bg-rose-500/10 px-2 py-1 rounded flex items-center gap-1"
                      >
                        <Trash2 size={11} /> Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {visibleItemCodes.length > 0 && (
            <div className="surface rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 border-b border-neutral-900 flex items-center gap-2 bg-neutral-950">
                <Camera size={13} className="text-amber-400" />
                <span className="text-xs font-semibold text-white tracking-wider uppercase">Individual Items</span>
                <span className="text-[11px] text-neutral-500 ml-auto">{visibleItemCodes.length}</span>
              </div>
              <div className="divide-y divide-neutral-900">
                {visibleItemCodes.map((code) => {
                  const it = items.find((i) => i.code === code);
                  if (!it) return null;
                  const qty = directQtyByCode.get(code) || 0;
                  return (
                    <div key={code} className="px-4 py-3 flex items-center gap-3 row-hover">
                      <Thumb src={it.image} name={it.name} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white">
                          {it.name}{' '}
                          {qty > 1 && (
                            <span className="text-amber-300 text-[11px] ml-1" data-testid={`item-qty-${code}`}>
                              × {qty}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-neutral-500 flex items-center gap-2">
                          <span className="font-mono">{it.code}</span>
                          <span className="cat-dot" style={{ background: categoryColor(it.category) }} />
                          <span>{it.category}</span>
                        </div>
                      </div>
                      {/* Qty +/- controls — only shown when total_qty > 1 */}
                      {it.totalQty > 1 && (
                        <div className="flex items-center gap-1 mr-1">
                          <button
                            onClick={async () => {
                              try {
                                await projectRemoveItem(project.id, code);
                                if (qty <= 1) toast.success(`${it.name} removed`);
                              } catch (e) {
                                toast.error(e.message || 'Could not update');
                              }
                            }}
                            className="h-6 w-6 rounded border border-neutral-800 text-neutral-300 hover:bg-neutral-900 text-xs"
                            data-testid={`item-dec-${code}`}
                            aria-label="Decrease quantity"
                          >
                            –
                          </button>
                          <span className="text-xs text-white tabular-nums px-1 min-w-[1.5rem] text-center">
                            {qty}
                          </span>
                          <button
                            onClick={async () => {
                              try {
                                await projectAddItem(project.id, code);
                              } catch (e) {
                                toast.error(e.message || 'Could not add another');
                              }
                            }}
                            className="h-6 w-6 rounded border border-neutral-800 text-neutral-300 hover:bg-neutral-900 text-xs"
                            data-testid={`item-inc-${code}`}
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                      )}
                      {dayCount > 1 && (
                        <DaySpanBadge
                          project={project}
                          code={code}
                          onProjectUpdate={handleSpanSaved}
                        />
                      )}
                      <button
                        onClick={async () => {
                          // Full remove: drop every occurrence by looping the API
                          for (let i = 0; i < qty; i++) {
                            await projectRemoveItem(project.id, code);
                          }
                          toast.success(`${it.name} removed`);
                        }}
                        className="text-[11px] text-rose-400 hover:bg-rose-500/10 px-2 py-1 rounded flex items-center gap-1"
                        data-testid={`item-remove-${code}`}
                      >
                        <Trash2 size={11} /> Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <AddGearDialog open={addOpen} onClose={() => setAddOpen(false)} project={project} />
    </div>
  );
}
